import { useTable } from '@tanstack/react-table';
import type {
  RowSelectionState,
  Updater,
  ColumnFiltersState,
  SortingState,
} from '@tanstack/react-table';
import uniqBy from 'lodash-es/uniqBy';
import { useMemo, useState } from 'react';

import {
  useFetchProducts,
  useGlobalProductSearch,
  useGlobalProductSearchFilters,
  useProductSearchFilters,
  useProductsById,
  toGlobalProduct,
  toStoreProduct,
} from '@jane/business-admin/data-access';
import type { SelectableProduct } from '@jane/business-admin/data-access';
import type { ProductFilters } from '@jane/shared-b2b/components';
import {
  columnFiltersToFilters,
  getSelectedProductIds,
  IN_STOCK,
  ConditionSelectionTypeRadio,
  ProductSelectModal,
  productSelectFeatures,
  toInStock,
  useConditionSelection,
  useProductSelectColumns,
} from '@jane/shared-b2b/components';

const asArray = (value: string | string[] | undefined): string[] =>
  Array.isArray(value) ? value : [];

const asQuery = (value: string | string[] | undefined): string =>
  typeof value === 'string' ? value : '';

const joined = (values: string[]): string | undefined =>
  values.length > 0 ? values.join(',') : undefined;

/** Operators pick products by name, not by catalog id */
const COLUMN_VISIBILITY = { id: false };

/**
 * The global catalog can't filter on weight: its options include `price_each`,
 * which the search endpoint rejects, and the filter is unimplemented anyway.
 */
const GLOBAL_UNFILTERABLE: ProductFilters[] = ['weight'];

const DEFAULT_COLUMN_FILTERS: ColumnFiltersState = [
  { id: 'outOfStock', value: [IN_STOCK] },
];

// TODO: Nothing here is specials-specific. Move this and useConditionSections
// to components/lib/shared/conditions/ when menu rows adopt the conditions editor.
export interface ProductsModalProps {
  isGlobal: boolean;
  orgId?: number;
  storeId: string;
}

/**
 * The catalog picker for the products row. Selection is held as ids, never as
 * table rows, so products on unfetched pages survive Save.
 */
export const ProductsModal = ({
  isGlobal,
  orgId,
  storeId,
}: ProductsModalProps) => {
  const {
    activeSection,
    activeValue,
    save,
    selectionType,
    setSelectionType,
    updateValue,
  } = useConditionSelection();

  const [isReviewing, setIsReviewing] = useState(false);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>(
    DEFAULT_COLUMN_FILTERS
  );

  const { brand, category, lineage, name, outOfStock, subcategory, weight } =
    columnFiltersToFilters(columnFilters);

  const inStock = toInStock(asArray(outOfStock));

  const selectedIds = useMemo(
    () => (Array.isArray(activeValue) ? activeValue.map(String) : []),
    [activeValue]
  );

  const storeSearch = useFetchProducts({
    enabled: !isGlobal,
    params: {
      brand: joined(asArray(brand)),
      category: joined(asArray(category)),
      in_stock: inStock,
      // Only needed to widen the default in-stock query back to everything
      include_out_of_stock: inStock === undefined,
      lineage: joined(asArray(lineage)),
      query: asQuery(name) || undefined,
      subcategory: joined(asArray(subcategory)),
      weight: joined(asArray(weight)),
    },
    storeId,
  });

  const globalSearch = useGlobalProductSearch({
    brand: joined(asArray(brand)),
    enabled: isGlobal,
    includeHidden: true,
    in_stock: inStock,
    kind: joined(asArray(category)),
    lineage: joined(asArray(lineage)),
    name: asQuery(name) || undefined,
    retail_org_id: orgId,
    subtype: joined(asArray(subcategory)),
    weight: joined(asArray(weight)),
  });

  const storeFilters = useProductSearchFilters({
    category: asArray(category),
    enabled: !isGlobal,
    storeId,
    subcategory: asArray(subcategory),
  });

  const globalFilters = useGlobalProductSearchFilters({
    enabled: isGlobal,
    kind: asArray(category),
    retail_org_id: orgId,
  });

  const search = isGlobal ? globalSearch : storeSearch;
  const filterQuery = isGlobal ? globalFilters : storeFilters;

  /** Only the store catalog returns every id matching the current filters */
  const canResolveEveryMatch = !isGlobal;

  const firstPage = search.data?.pages[0];

  /** Include and exclude show different lists, so switching closes review mode */
  const changeSelectionType = (type: typeof selectionType) => {
    setIsReviewing(false);
    setSelectionType(type);
  };

  /**
   * Review mode shows the whole selection, so it fetches the in-progress draft
   * rather than what the group last saved.
   */
  const { isLoading: isLoadingReviewed, products: reviewedProducts } =
    useProductsById({
      enabled: isReviewing,
      ids: selectedIds,
      isGlobal,
      orgId,
      storeId,
    });

  /**
   * Browsing lists only what the catalog returned, so filtering a row out hides
   * it. Reviewing adds every selected product, fetched or not.
   */
  const products = useMemo(() => {
    const fetched = (search.data?.pages ?? []).flatMap((page) =>
      isGlobal
        ? page.products.map(toGlobalProduct)
        : page.products.map(toStoreProduct)
    );

    return isReviewing
      ? uniqBy([...reviewedProducts, ...fetched], 'id')
      : fetched;
  }, [isGlobal, isReviewing, reviewedProducts, search.data]);

  /** Store catalog only; the global search returns no `all_product_ids` */
  const allProductIds = useMemo(
    () =>
      isReviewing
        ? reviewedProducts.map(({ id }) => id)
        : (firstPage?.meta.all_product_ids ?? []).map(
            (product: { product_id: number }) => product.product_id.toString()
          ),
    [firstPage, isReviewing, reviewedProducts]
  );

  const filters = useMemo(
    () => ({
      brand: filterQuery.data?.filters.brands ?? [],
      category: filterQuery.data?.filters.categories ?? [],
      lineage: filterQuery.data?.filters.lineages ?? [],
      subcategory: filterQuery.data?.filters.subcategories ?? [],
      weight: filterQuery.data?.filters.weights ?? [],
    }),
    [filterQuery.data]
  );

  const columns = useProductSelectColumns<SelectableProduct>({
    allProductIds: canResolveEveryMatch ? allProductIds : undefined,
    showSelectAllCheckbox: canResolveEveryMatch,
    filters,
    unfilterable: isGlobal ? GLOBAL_UNFILTERABLE : undefined,
    /**
     * Browsing counts every catalog match, not just the pages fetched.
     * Reviewing counts the draft, which includes ids whose rows never loaded.
     */
    productCount: isReviewing
      ? selectedIds.length
      : (firstPage?.meta.total ?? 0),
  });

  /** Derived from the draft, so the table holds no selection state of its own */
  const rowSelection = useMemo(
    () => getSelectedProductIds(selectedIds),
    [selectedIds]
  );

  const onRowSelectionChange = (updater: Updater<RowSelectionState>) => {
    const next =
      typeof updater === 'function' ? updater(rowSelection) : updater;

    updateValue(Object.keys(next).filter((id) => next[id]));
  };

  const productTable = useTable({
    features: productSelectFeatures,
    columns,
    data: products,
    state: {
      columnFilters,
      columnVisibility: COLUMN_VISIBILITY,
      rowSelection,
      sorting,
    },
    onColumnFiltersChange: setColumnFilters,
    onRowSelectionChange,
    onSortingChange: setSorting,
    getRowId: (row) => row.id,
    enableRowSelection: true,
  });

  return (
    <ProductSelectModal<SelectableProduct>
      canSelectAll={canResolveEveryMatch}
      allProductIds={canResolveEveryMatch ? allProductIds : undefined}
      freezeNameColumn
      hasReviewMode
      reviewMode={isReviewing}
      onReviewModeChange={setIsReviewing}
      isFetchingNextPage={search.isFetchingNextPage}
      isLoadingFilters={filterQuery.isFetching}
      isLoadingProducts={search.isPending || isLoadingReviewed}
      onNextPage={search.fetchNextPage}
      searchPlaceholder={`Search name${isGlobal ? '' : ', brand,'} or POS name`}
      searchWidth={300}
      onSaveClick={save}
      productTable={productTable}
      headerContent={
        activeSection?.allowExcludes && (
          <ConditionSelectionTypeRadio
            noun="products"
            onChange={changeSelectionType}
            value={selectionType}
          />
        )
      }
    />
  );
};
